import { db, auth } from '../firebaseConfig.js';
import { collection, query, where, deleteDoc, doc, serverTimestamp, getCountFromServer, updateDoc, increment, setDoc, getDoc, getDocsFromCache } from 'firebase/firestore';

// Get likes for a reason and whether current user liked
export async function getReasonLikes(reasonId, userId) {
  const lowQuota = process.env.REACT_APP_LOW_QUOTA_MODE === 'true';
  const likesRef = collection(db, 'solution_reason_likes');
  let likes = 0;
  const countQuery = query(likesRef, where('reasonId', '==', reasonId));
  // Try cache-first count to avoid server reads
  try {
    const cached = await getDocsFromCache(countQuery);
    if (cached && !cached.empty) {
      likes = cached.size;
    }
  } catch (_) { /* ignore cache miss */ }
  // If not low quota and likes still 0, try server aggregation
  if (!lowQuota && likes === 0) {
    try {
      const totalCountSnap = await getCountFromServer(countQuery);
      likes = totalCountSnap.data().count || 0;
    } catch (e) {
      console.warn('[getReasonLikes] getCountFromServer failed:', e?.code || e?.message || e);
    }
  }
  // Check if the current user liked (single doc existence check)
  let userLiked = false;
  if (userId) {
    const likeId = `${reasonId}_${userId}`;
    const likeDoc = await getDoc(doc(db, 'solution_reason_likes', likeId));
    userLiked = likeDoc.exists();
  }
  return { reasonId, likes, userLiked };
}

// Like a reason
export async function likeReason(reasonId, userId) {
  const authUid = auth?.currentUser?.uid;
  if (!authUid) {
    const err = new Error('Not authenticated');
    console.error('[likeReason] No authenticated Firebase user present');
    throw err;
  }
  const uid = authUid; // override provided userId to satisfy rules
  // Prevent duplicate likes
  const likeId = `${reasonId}_${uid}`;
  const likeDocRef = doc(db, 'solution_reason_likes', likeId);
  const existing = await getDoc(likeDocRef);
  if (!existing.exists()) {
    try { await auth.currentUser.getIdToken(); } catch {}
    console.debug('[likeReason] Creating like', { reasonId, uid, likeId });
    await setDoc(likeDocRef, { reasonId, userId: uid, timestamp: serverTimestamp() });
    // Best-effort increment of likesCount on parent reason doc
    try {
      await updateDoc(doc(db, 'solution_reasons', reasonId), { likesCount: increment(1) });
    } catch (e) {
      console.warn('[likeReason] likesCount increment skipped:', e?.code || e?.message || e);
    }
  }
  return getReasonLikes(reasonId, uid);
}

// Unlike a reason
export async function unlikeReason(reasonId, userId) {
  const authUid = auth?.currentUser?.uid;
  if (!authUid) {
    const err = new Error('Not authenticated');
    console.error('[unlikeReason] No authenticated Firebase user present');
    throw err;
  }
  const uid = authUid; // override provided userId
  const likeId = `${reasonId}_${uid}`;
  const likeDocRef = doc(db, 'solution_reason_likes', likeId);
  const existing = await getDoc(likeDocRef);
  if (existing.exists()) {
    try { await auth.currentUser.getIdToken(); } catch {}
    console.debug('[unlikeReason] Deleting like', { reasonId, uid, likeId });
    await deleteDoc(likeDocRef);
    // Best-effort decrement of likesCount on parent reason doc
    try {
      await updateDoc(doc(db, 'solution_reasons', reasonId), { likesCount: increment(-1) });
    } catch (e) {
      console.warn('[unlikeReason] likesCount decrement skipped:', e?.code || e?.message || e);
    }
  }
  return getReasonLikes(reasonId, uid);
}
